import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, User } from 'lucide-react';
import Skeleton from '../../../components/common/Skeleton';
import {
    itaProgramService,
    ItaProgram,
} from '../../../services/itaService';
import Toast, { ToastMessage } from '../../../components/common/Toast';
import Pagination from '../../../components/common/Pagination';
import NoDataFound from '../../../components/common/NoDataFound';
import FormControl from '../../../components/common/FormControl';
import extractApiError from '../../../utils/apiError';
import { ratStageLabel, ratStageBadgeClass } from '../../../utils/statusVariant';
import { useAuth } from '../../../contexts/AuthContext';
import ITATExecutionModal from '../../../components/itAssessment/ITATExecutionModal';
import { visibleForScope, isClosedProgram } from './workArchive';

interface WorkITATBodyProps {
    scope?: 'active' | 'archive';
    readOnly?: boolean;
}

const WorkITATBody: React.FC<WorkITATBodyProps> = ({
    scope = 'active',
    readOnly = false,
}) => {
    const { state } = useAuth();
    const myId = state.user?.id;
    const [programs, setPrograms] = useState<ItaProgram[]>([]);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState<number | null>(null);
    const [openIsChecker, setOpenIsChecker] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    // Deep-link from calendar drill-down: /work?tab=it-assessment&open=<id>
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const o = searchParams.get('open');
        if (!o) return;
        const id = parseInt(o, 10);
        if (Number.isFinite(id)) setOpenId(id);
        const next = new URLSearchParams(searchParams);
        next.delete('open');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            // Staff worklist: programs where I'm the maker OR the checker, so an
            // assigned checker sees the program after the maker submits it.
            const data = await itaProgramService.list({ mine: true });
            setPrograms(data);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load programs') });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => { setPage(1); }, [searchTerm, pageSize]);

    const scopedPrograms = visibleForScope(programs, p => isClosedProgram(p.status), scope);
    const filtered = scopedPrograms.filter(p =>
        !searchTerm ||
        p.program_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.template_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.target_label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.location_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const totalRecords = filtered.length;
    const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                    />
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Program</th>
                                        <th className="table-list-th">Asset / Application</th>
                                        <th className="table-list-th">Window</th>
                                        <th className="table-list-th">Maker</th>
                                        <th className="table-list-th">Checker</th>
                                        <th className="table-list-th">Progress</th>
                                        <th className="table-list-th table-list-cell--status table-list-th--right">Status</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && (
                                        <Skeleton.TableRows cols={['2xs', 'md', 'sm', 'sm', 'xs', 'xs', 'sm', { width: 'pill', align: 'right' }]} />
                                    )}
                                    {!loading && filtered.length === 0 && (
                                        <tr>
                                            <td colSpan={8} className="table-list-cell">
                                                <NoDataFound
                                                    title={scope === 'archive' ? 'No Closed IT Assessments' : 'No IT Assessment Programs Found'}
                                                    message={scope === 'archive' ? 'No closed IT assessments.' : 'No IT assessment programs assigned to you.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && paginatedRows.map((p, idx) => {
                                        // Checker = assigned checker and not also the maker. Drives the
                                        // modal mode (read-only inputs + Approve, vs maker's score + Submit).
                                        const isChecker = p.checker_id === myId && p.maker_id !== myId;
                                        return (
                                        <tr
                                            key={p.id}
                                            className="table-list-row"
                                            onClick={() => { setOpenIsChecker(isChecker); setOpenId(p.id); }}
                                        >
                                            <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="table-list-cell">
                                                <span className="table-list-cell-title">{p.program_name}</span>
                                            </td>
                                            <td className="table-list-cell">
                                                {p.target_code ? `${p.target_code} — ` : ''}{p.target_label || '—'}
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="text-xs text-gray-600">
                                                    {FormControl.formatDate(p.start_date)} → {FormControl.formatDate(p.end_date)}
                                                </span>
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="flex items-center gap-1 text-sm">
                                                    <User size={12} className="text-gray-400" />
                                                    {p.maker_name || '—'}
                                                </span>
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="flex items-center gap-1 text-sm">
                                                    <User size={12} className="text-gray-400" />
                                                    {p.checker_name || '—'}
                                                </span>
                                            </td>
                                            <td className="table-list-cell">
                                                {(() => {
                                                    const total = p.control_count ?? 0;
                                                    const done = p.completed_count ?? 0;
                                                    const pct = Math.round((done / Math.max(total, 1)) * 100);
                                                    const full = total > 0 && done >= total;
                                                    return (
                                                        <div className="step-progress-bar">
                                                            <div className="step-progress-bar__track">
                                                                <div
                                                                    className={`step-progress-bar__fill ${full ? 'bg-green-400' : 'bg-amber-400'}`}
                                                                    style={{ width: `${pct}%` }}
                                                                />
                                                            </div>
                                                            <span className="step-progress-bar__text">{done}/{total}</span>
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                            <td className="table-list-cell table-list-cell--status table-list-cell--right">
                                                <span className={`inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium ${ratStageBadgeClass(p.status)}`}>
                                                    {ratStageLabel(p.status)}
                                                </span>
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            recordLabel="programs"
                        />
                    </div>
                </div>
            </div>

            <Toast toast={toast} onClose={() => setToast(null)} />

            {openId !== null && (
                <ITATExecutionModal
                    programId={openId}
                    onClose={() => setOpenId(null)}
                    onSaved={refresh}
                    readOnly={readOnly}
                    isChecker={openIsChecker}
                />
            )}
        </div>
    );
};

export default WorkITATBody;
